import { DataDumpRow, ProductionSegmentType } from "types/types";
import { useDataDumpRows } from "components/Dashboard/HubletView/SingleTimeline/singleTimelineStore";
import { useTimelineTooltipStore } from "../timelineTooltipStore";
import { isNil } from "lodash";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { FULL_DATE_TIME_ZONE, ONE_MINUTE_OF_MS } from "utils/timeUtils";
import { useFormatZoned } from "utils/customHooks/genericHooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAtom } from "jotai";
import { customerInfoJsonAtom } from "reports/utils/hooks/useCustomerInfoJson";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { getAverageRunRateForDataDumpRow } from "reports/utils/metricUtils";
import { GoToThisRangeMenu } from "./AboutPanel";

let prevScrollHeight = 0;

/** Displays metadata about the segment */
export function MoreInfoPanel({
  explanations,
  traceability,
  selectedProdSegment,
}: {
  explanations?: string[];
  traceability?: string[];
  selectedProdSegment: ProductionSegmentType;
}) {
  const comments = [...(explanations ?? []), ...(traceability ?? [])];
  const hasMultipleComments = comments.length > 1;
  const [
    {
      general: { timelineTooltipShowCounts, timelineTooltipAdditionalMetrics },
    },
  ] = useFlightConfig();
  const [allDataDumpRows] = useDataDumpRows();
  const [{ machemetryId }] = useTimelineTooltipStore();
  const dataDumpRow = useMemo(() => {
    const dataDumpRows =
      machemetryId && allDataDumpRows[machemetryId]
        ? allDataDumpRows[machemetryId]
        : [];
    return (
      findMatching(dataDumpRows, selectedProdSegment, true) ??
      findMatching(dataDumpRows, selectedProdSegment, false)
    );
  }, [selectedProdSegment, allDataDumpRows, machemetryId]);
  const operatorId = dataDumpRow?.operatorId;
  const [allCustomerInfoJson] = useAtom(customerInfoJsonAtom);
  const ckey = useCustomerKey();
  const customerInfoJson = allCustomerInfoJson[ckey];
  const operatorGroups = customerInfoJson?.operatorGroups;
  const operator =
    operatorGroups && operatorId ? operatorGroups[operatorId] : null;
  const operatorName = operator?.name;
  const skuCode = dataDumpRow?.skuCode;
  const skuName = dataDumpRow?.skuName;
  // const skuScale = dataDumpRow?.skuScale;
  const targetRate = dataDumpRow?.targetRate;
  const rateDenominator = dataDumpRow?.rateDenominator;
  // const teamSize = dataDumpRow?.teamSize;

  const netCount = dataDumpRow?.netOutput;
  const rejectCount = dataDumpRow?.rejectOutput;
  const grossCount = dataDumpRow?.grossOutput;
  const segmentDuration = dataDumpRow
    ? Number(dataDumpRow.lt) - Number(dataDumpRow.ge)
    : 0;
  const segmentDurationMinutes = segmentDuration / ONE_MINUTE_OF_MS;
  const formatZoned = useFormatZoned();

  const [skuRateDenominatorDisplay, skuRateDemonatorDivisor] = !skuCode
    ? [null, 1]
    : rateDenominator === 3600000
      ? ["hr", 60]
      : rateDenominator === 1000
        ? ["sec", 1 / 60]
        : ["min", 1];

  // maintain previous scrollHeight on mount
  const ref = useRef<HTMLDivElement>(null);
  // and if you scroll to the bottom, keep it scrolled to the bottom even at variable heights
  const [didScrollToBottom, setDidScrollToBottom] = useState(false);
  useEffect(() => {
    if (!ref.current) {
      return;
    }
    // if we were scrolled all the way to the bottom, stick to bottom even if height is different
    if (didScrollToBottom) {
      prevScrollHeight = ref.current.scrollHeight;
    }
    ref.current?.scrollTo(0, prevScrollHeight);
  }, [selectedProdSegment, didScrollToBottom]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    prevScrollHeight = e.currentTarget.scrollTop;
    if (!ref.current) {
      return;
    }
    const scrolledAllTheWayToBottom =
      ref.current.scrollTop ===
      ref.current.scrollHeight - ref.current.clientHeight;
    setDidScrollToBottom(scrolledAllTheWayToBottom);
  }, []);
  return (
    <div
      ref={ref}
      className={`moreInfo panel${hasMultipleComments ? " multiComment" : ""}`}
      onWheel={handleScroll}
      onScroll={handleScroll}
    >
      <div className="sectionHeader">
        <div className="timerangeShort">
          {formatZoned(
            Number(selectedProdSegment.range.ge),
            FULL_DATE_TIME_ZONE
          )}{" "}
          —{" "}
          {formatZoned(
            Number(selectedProdSegment.range.lt || Date.now()),
            FULL_DATE_TIME_ZONE
          )}
          <div style={{ position: "absolute", right: 0, top: -2 }}>
            <GoToThisRangeMenu />
          </div>
        </div>
      </div>
      <div style={{ marginTop: "5px" }} />
      {timelineTooltipShowCounts && (
        <div
          className="textAlignCenter centeredGrid timeline-tooltip-counts"
          style={{ padding: "4px 8px" }}
        >
          {skuCode ? (
            <>
              <p
                className="datetime skuOperatorTimelineInfoRow"
                style={{
                  height: "fit-content",
                  whiteSpace: "normal",
                  lineHeight: 1,
                  marginBottom: 5,
                }}
              >
                <strong>SKU:</strong>{" "}
                <span style={{ textAlign: "right" }}>{skuName ?? "--"}</span>
              </p>
              <p className="datetime skuOperatorTimelineInfoRow">
                <strong>Code:</strong> <span>{skuCode}</span>
              </p>
              <p className="datetime skuOperatorTimelineInfoRow">
                <strong>
                  Target Rate {`(/${skuRateDenominatorDisplay})`}:
                </strong>{" "}
                <span>{targetRate ?? "--"}</span>
              </p>
              <p className="datetime skuOperatorTimelineInfoRow">
                <strong>
                  Target Count{" "}
                  {`(${(
                    segmentDurationMinutes / skuRateDemonatorDivisor
                  ).toFixed(2)}${skuRateDenominatorDisplay})`}
                  :
                </strong>{" "}
                <span>
                  {targetRate && segmentDurationMinutes
                    ? (
                        (targetRate * segmentDurationMinutes) /
                        skuRateDemonatorDivisor
                      ).toFixed(2)
                    : "--"}
                </span>
              </p>
            </>
          ) : null}
          {operatorId ? (
            <div className="datetime skuOperatorTimelineInfoRow">
              <strong>Operator:</strong>{" "}
              {operator?.avatar_letters ? (
                <div
                  className="operatorAvatar"
                  style={{
                    background: operator.avatar_letters_bg,
                    color: operator.avatar_letters_fg,
                  }}
                >
                  {operator.avatar_letters}
                </div>
              ) : null}{" "}
              <span>{operatorName ?? "--"}</span>
            </div>
          ) : null}
          <p className="datetime skuOperatorTimelineInfoRow">
            <strong>Gross Count:</strong>{" "}
            <span>
              {grossCount || grossCount === 0 ? grossCount.toFixed(2) : "--"}
            </span>
          </p>
          <p className="datetime skuOperatorTimelineInfoRow">
            <strong>Reject Count:</strong>{" "}
            <span>
              {rejectCount || rejectCount === 0 ? rejectCount.toFixed(2) : "--"}
            </span>
          </p>
          <p className="datetime skuOperatorTimelineInfoRow">
            <strong>Net Count:</strong>{" "}
            <span>
              {netCount || netCount === 0 ? netCount.toFixed(2) : "--"}
            </span>
          </p>
        </div>
      )}
      {timelineTooltipAdditionalMetrics &&
        timelineTooltipAdditionalMetrics.map(
          ({ dataDumpKey, displayName, units }, idx) => {
            let valueDisplay = dataDumpRow?.[dataDumpKey] ?? "--";

            // exceptions for specific metrics
            if (dataDumpRow && dataDumpKey === "Avg. Run Rate") {
              valueDisplay =
                getAverageRunRateForDataDumpRow(dataDumpRow)?.toFixed(2);
            }

            return (
              <div
                key={idx}
                className="textAlignCenter centeredGrid timeline-tooltip-counts"
                style={{ padding: "4px 8px" }}
              >
                <p className="datetime skuOperatorTimelineInfoRow">
                  <strong>{displayName}:</strong>{" "}
                  <span>
                    {valueDisplay}
                    {units}
                  </span>
                </p>
              </div>
            );
          }
        )}
      {comments.length > 0 && (
        <div style={{ padding: 8 }}>
          <strong>Metadata:</strong>
          <div
            className={
              hasMultipleComments
                ? "bulletList"
                : "textAlignCenter centeredGrid"
            }
            style={{ textAlign: "left" }}
          >
            <ul>
              {explanations?.map((e, idx) => <li key={idx}>{e}</li>)}
              {traceability?.map((t, idx) => (
                <li key={idx} style={{ opacity: 0.6 }}>
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function findMatching(
  subTimelineSegments: DataDumpRow[],
  selectedProdSegment: ProductionSegmentType,
  exact = false
): DataDumpRow | undefined {
  // always try to find exact match first
  const filteredSeg = findMatchings(
    subTimelineSegments,
    selectedProdSegment,
    exact
  );

  // if a segment matches by matchesRangeWithOverlap, it's two or more segments whose counts we need to combine into one
  if (!exact && filteredSeg && filteredSeg.length > 1) {
    return filteredSeg.reduce((acc, seg) => {
      return {
        ...acc,
        netOutput: (acc.netOutput || 0) + (seg.netOutput || 0),
        grossOutput: (acc.grossOutput || 0) + (seg.grossOutput || 0),
        rejectOutput: (acc.rejectOutput || 0) + (seg.rejectOutput || 0),
        ge: `${Math.min(Number(acc.ge), Number(seg.ge))}`,
        lt: `${Math.max(Number(acc.lt), Number(seg.lt))}`,
      };
    });
  }

  return filteredSeg && filteredSeg.length > 0 ? filteredSeg[0] : undefined;
}

function findMatchings(
  subTimelineSegments: DataDumpRow[],
  selectedProdSegment: ProductionSegmentType,
  exact = false
): DataDumpRow[] | undefined {
  const lastSegment = subTimelineSegments[
    subTimelineSegments.length - 1
  ] as any;
  const lastSegmentLt = lastSegment?.lt;

  const now = Date.now();
  return (subTimelineSegments as any[]).filter(segment => {
    const ge = Number(segment.ge);
    const lt = Number(segment.lt);
    const [selectedGe, selectedLt] = [
      isNil(selectedProdSegment.range.ge)
        ? undefined
        : Number(selectedProdSegment.range.ge),
      isNil(selectedProdSegment.range.lt)
        ? undefined
        : Number(selectedProdSegment.range.lt),
    ];
    // segment within ( lt and ge)
    const matchesRange =
      !isNil(selectedLt) &&
      !isNil(selectedGe) &&
      (exact
        ? ge === selectedGe && lt === selectedLt
        : ge < selectedLt && lt > selectedGe);
    // open-ended segment (dataDumpRows segments usually don't have undefined lt like prodSegments)
    const matchesOpenEndedRange =
      ((isNil(lt) || lt >= now) && (isNil(selectedLt) || selectedLt >= now)) ||
      ((isNil(lt) || lt >= lastSegmentLt) &&
        (isNil(selectedLt) || selectedLt >= lastSegmentLt));

    // sometimes a production segment's range will contain two count segments in sequence,
    // in this case we need to return both segments
    const matchesRangeWithOverlap =
      !isNil(selectedLt) &&
      !isNil(selectedGe) &&
      !exact &&
      // case 1: segment within, left-side: matches ge but contains lt
      ((selectedGe === ge && lt < selectedLt) ||
        // Case 2: segment within, right-side: matches lt but contains ge
        (selectedGe < ge && lt === selectedLt) ||
        // Case 3: selected segment contains segment
        (selectedGe < ge && lt < selectedLt));
    return matchesRange || matchesOpenEndedRange || matchesRangeWithOverlap;
  });
}
